import React, { useState } from 'react';
import CustomerModal from './CustomerModal';
import { projectPaymentSummary, projectAggregateStatus, aggregateStatusStyle } from '../paymentsUtil';

function typeBadge(type) {
  const map = { Architect: 'badge-architect', Contractor: 'badge-contractor', Individual: 'badge-individual', 'Planning Consultant': 'badge-planning' };
  return <span className={`badge ${map[type] || 'badge-individual'}`}>{type}</span>;
}

function statusBadge(status) {
  const map = { Active: 'badge-active', Completed: 'badge-completed', Pending: 'badge-pending', Rejected: 'badge-rejected', Disputed: 'badge-disputed' };
  return <span className={`badge ${map[status] || ''}`}>{status}</span>;
}

const fmt = n => n != null ? `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 0 })}` : '—';

export default function CustomerDetailModal({ customer, projects, onClose, onRefresh, user }) {
  const [editing, setEditing] = useState(false);
  const custProjects = projects.filter(p => p.customer_id === customer.id);
  const summaries = custProjects.map(p => ({ p, s: projectPaymentSummary(p), agg: projectAggregateStatus(p) }));
  const totalAgreed = summaries.reduce((s, x) => s + x.s.agreed, 0);
  const totalPaid = summaries.reduce((s, x) => s + x.s.paid, 0);
  const totalPending = summaries.reduce((s, x) => s + x.s.pending, 0);
  const totalOverdue = summaries.reduce((s, x) => s + x.s.overdue, 0);
  const overdueProjectCount = summaries.filter(x => x.agg.key === 'overdue').length;

  if (editing) return (
    <CustomerModal
      customer={customer}
      onClose={() => setEditing(false)}
      onSaved={() => { setEditing(false); onRefresh(); }}
      user={user}
    />
  );

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <h2 style={{ fontSize: 16 }}>{customer.full_name}</h2>
              {typeBadge(customer.type)}
              {customer.starred && <span style={{ fontSize: 16 }}>⭐</span>}
            </div>
            {customer.company && <p style={{ fontSize: 13, color: 'var(--text3)' }}>{customer.company}</p>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => setEditing(true)}>Edit</button>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
            <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Email</div>
              {customer.email ? <a href={`mailto:${customer.email}`} style={{ color: 'var(--blue)', fontSize: 13 }}>{customer.email}</a> : <span style={{ color: 'var(--text3)', fontSize: 13 }}>—</span>}
            </div>
            <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Phone</div>
              <span style={{ fontSize: 13 }}>{customer.phone || '—'}</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Projects</div>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font)' }}>{custProjects.length}</div>
            </div>
            <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Total Agreed</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--font)' }}>{fmt(totalAgreed)}</div>
            </div>
            <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Total Paid</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--green)', fontFamily: 'var(--font)' }}>{fmt(totalPaid)}</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
            <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Outstanding (Balance)</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: totalPending > 0 ? 'var(--text)' : 'var(--text3)', fontFamily: 'var(--font)' }}>{fmt(totalPending)}</div>
            </div>
            <div style={{ background: totalOverdue > 0 ? 'var(--red-dim)' : 'var(--bg3)', borderRadius: 'var(--radius)', padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Overdue</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: totalOverdue > 0 ? 'var(--red)' : 'var(--text3)', fontFamily: 'var(--font)' }}>{fmt(totalOverdue)}</div>
              {overdueProjectCount > 0 && (
                <div style={{ fontSize: 10, color: 'var(--red)', marginTop: 2 }}>{overdueProjectCount} project{overdueProjectCount === 1 ? '' : 's'}</div>
              )}
            </div>
          </div>

          {customer.notes && (
            <div style={{ marginBottom: 20, background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Notes</div>
              <p style={{ fontSize: 13, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{customer.notes}</p>
            </div>
          )}

          {custProjects.length > 0 && (
            <>
              <label style={{ fontSize: 12, color: 'var(--accent)', display: 'block', marginBottom: 10 }}>PROJECTS</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {summaries.map(({ p, agg }) => {
                  const aggStyle = aggregateStatusStyle(agg.key);
                  return (
                    <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--accent)' }}>{p.project_code}</span>
                          {statusBadge(p.status)}
                          {agg.key !== 'none' && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                              color: aggStyle.color, background: aggStyle.bg,
                              padding: '2px 7px', borderRadius: 3,
                            }}>{agg.label.toUpperCase()}</span>
                          )}
                        </div>
                        <span style={{ fontSize: 13, color: 'var(--text)' }}>{p.project_name}</span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{fmt(p.financials?.[0]?.agreed_fee)}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{p.service_type}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
